import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

type Item = [string, string];
type Group = { id: string; label: string; items: Item[] };

/** Entrée hors bloc, toujours visible en tête. */
const HOME: Item = ['/', 'Accueil'];

/** Les 25 destinations réparties en 6 blocs thématiques. */
const GROUPS: Group[] = [
  {
    id: 'apprendre',
    label: 'Apprendre',
    items: [
      ['/catalogue', 'Catalogue'],
      ['/learn', 'Mes cours'],
      ['/study-path', 'Mon parcours'],
      ['/exams', 'Examens'],
      ['/credentials', 'Mes documents'],
    ],
  },
  {
    id: 'live',
    label: 'Live & Laboratoires',
    items: [
      ['/live', 'Live'],
      ['/live-admin', 'Salles Live'],
      ['/labs', 'Laboratoires'],
      ['/media', 'Enregistrements'],
    ],
  },
  {
    id: 'biblio',
    label: 'Bibliothèque',
    items: [
      ['/bookstore', 'Librairie'],
      ['/publishing', 'Publication'],
      ['/media-admin', 'Médias'],
    ],
  },
  {
    id: 'commerce',
    label: 'Commerce',
    items: [
      ['/sales', 'Ventes'],
      ['/offerings', 'Offres'],
      ['/payments', 'Paiements'],
      ['/wallet', 'Wallet'],
      ['/finance', 'Finance'],
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    items: [
      ['/academic', 'Académique'],
      ['/regime', 'Régime'],
      ['/institutions', 'Institutions'],
      ['/banges', 'Banges'],
      ['/credentials-admin', 'Diplômes'],
      ['/users', 'Comptes'],
      ['/support', 'Accompagnement'],
    ],
  },
  {
    id: 'systeme',
    label: 'Système',
    items: [['/operations', 'État système']],
  },
];

const STORAGE_KEY = 'eduversa:nav:open';

function readStored(): string[] | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

export function AppShell() {
  const { principal, logout } = useAuth();
  const { pathname } = useLocation();

  /** Bloc contenant la page courante — il doit être ouvert. */
  const activeGroup = useMemo(() => {
    const hit = GROUPS.find((g) =>
      g.items.some(([to]) => pathname === to || pathname.startsWith(to + '/')),
    );
    return hit?.id ?? null;
  }, [pathname]);

  const initialOpen: string[] = readStored() ?? (activeGroup ? [activeGroup] : ['apprendre']);
  const [open, setOpen] = useState<string[]>(initialOpen);

  /** Ouvre automatiquement le bloc de la page courante, sans fermer les autres. */
  useEffect(() => {
    if (activeGroup && !open.includes(activeGroup)) {
      setOpen((prev) => [...prev, activeGroup]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    } catch {
      /* stockage indisponible : sans conséquence */
    }
  }, [open]);

  const toggle = (id: string) =>
    setOpen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <img
            className="brand-mark"
            src="/symbole-circulaire-512.png"
            alt=""
            width={40}
            height={40}
          />
          <div>
            <strong>EDUVERSA</strong>
            <small>Learn without boundaries</small>
          </div>
        </div>

        <nav aria-label="Navigation principale">
          <NavLink to={HOME[0]} end className="nav-link">
            {HOME[1]}
          </NavLink>

          {GROUPS.map((group) => {
            const isOpen = open.includes(group.id);
            const panelId = `navgroup-${group.id}`;
            return (
              <div className="nav-group" key={group.id}>
                <button
                  type="button"
                  className="nav-group-toggle"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggle(group.id)}
                >
                  <span>{group.label}</span>
                  <span className="nav-chevron" aria-hidden="true">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div id={panelId} className="nav-group-items" hidden={!isOpen}>
                  {group.items.map(([to, label]) => (
                    <NavLink key={to} to={to} className="nav-link">
                      {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <small>{principal?.role}</small>
          <button className="button ghost" onClick={logout}>
            Déconnexion
          </button>
        </div>
      </aside>

      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}
